/**
 * Edge Agent Monitor - Main Application
 *
 * Routes:
 * - /login - Google OAuth sign-in
 * - /agents - Agent selection (only Facility Audit active)
 * - /audit - Audit dashboard
 * - /settings - Facility settings
 * - /users - User management (admin only)
 */

import { useState, useEffect, createContext, useContext } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import {
  LogOut, Settings, Users, Activity, ChevronRight,
  Building2, Shield, Loader2, AlertCircle, Home, UserCheck, Package
} from 'lucide-react'
import { api } from './lib/api'
import AuditDashboard from './components/AuditDashboard'
import FacilitySettings from './components/FacilitySettings'
import UserManagement from './components/UserManagement'
import LeaseStatus from './components/LeaseStatus'

// =============================================================================
// Auth Context
// =============================================================================

const AuthContext = createContext(null)

export function useAuth() {
  return useContext(AuthContext)
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      api.setToken(token)
      fetchUser()
    } else {
      setLoading(false)
    }
  }, [])

  const fetchUser = async () => {
    try {
      const data = await api.get('/auth/me')
      setUser(data)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch user:', err)
      localStorage.removeItem('token')
      api.setToken(null)
      setError('Session expired. Please sign in again.')
    } finally {
      setLoading(false)
    }
  }

  const login = async (credential) => {
    try {
      setLoading(true)
      setError(null)
      const { token, user } = await api.post('/auth/google', { credential })
      localStorage.setItem('token', token)
      api.setToken(token)
      setUser(user)
      return true
    } catch (err) {
      console.error('Login failed:', err)
      setError(err.message || 'Login failed. Please try again.')
      return false
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('token')
    api.setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  )
}

// =============================================================================
// Protected Route Component
// =============================================================================

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') {
    return <Navigate to="/agents" replace />
  }

  return children
}

// =============================================================================
// Login Page
// =============================================================================

function LoginPage() {
  const { login, loading, error, user } = useAuth()
  const navigate = useNavigate()

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/agents')
    }
  }, [user, navigate])

  const handleGoogleSuccess = async (credentialResponse) => {
    const success = await login(credentialResponse.credential)
    if (success) {
      navigate('/agents')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-500 to-violet-500 mb-4 shadow-lg">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Edge Agent Monitor</h1>
          <p className="text-gray-500">Facility audit tool for Storable Edge</p>
        </div>

        {/* Login Card */}
        <div className="card shadow-soft">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign In</h2>
            <p className="text-sm text-gray-500">
              Use your <span className="text-indigo-600 font-medium">@storable.com</span> Google account
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            </div>
          ) : (
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.error('Google login failed')}
                theme="outline"
                size="large"
                width="300"
              />
            </div>
          )}

          <p className="mt-4 text-xs text-center text-gray-400">
            Only @storable.com email addresses can access this application
          </p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Agent Selection Page
// =============================================================================

function AgentSelectionPage() {
  const navigate = useNavigate()
  const [facilities, setFacilities] = useState([])
  const [facilityStats, setFacilityStats] = useState({})
  const [loadingFacilities, setLoadingFacilities] = useState(true)
  const [loadingStats, setLoadingStats] = useState({})
  const [selectedLeaseStatusFacility, setSelectedLeaseStatusFacility] = useState(null)

  // Fetch facilities on mount
  useEffect(() => {
    fetchFacilities()
  }, [])

  const fetchFacilities = async () => {
    try {
      const { facilities } = await api.get('/facilities')
      const activeFacilities = facilities.filter(f => f.is_active)
      setFacilities(activeFacilities)

      // Set first facility as default for lease status
      if (activeFacilities.length > 0 && !selectedLeaseStatusFacility) {
        setSelectedLeaseStatusFacility(activeFacilities[0])
      }

      // Fetch stats for each facility
      activeFacilities.forEach(f => {
        fetchFacilityStats(f.id)
      })
    } catch (err) {
      console.error('Failed to fetch facilities:', err)
    } finally {
      setLoadingFacilities(false)
    }
  }

  const fetchFacilityStats = async (facilityId) => {
    setLoadingStats(prev => ({ ...prev, [facilityId]: true }))
    try {
      const data = await api.get(`/facilities/${facilityId}/stats`)
      setFacilityStats(prev => ({ ...prev, [facilityId]: data.stats }))
    } catch (err) {
      console.error(`Failed to fetch stats for facility ${facilityId}:`, err)
      setFacilityStats(prev => ({ ...prev, [facilityId]: null }))
    } finally {
      setLoadingStats(prev => ({ ...prev, [facilityId]: false }))
    }
  }

  // Calculate totals across all facilities
  const totals = Object.values(facilityStats).reduce((acc, stats) => {
    if (stats) {
      acc.totalUnits += stats.totalUnits || 0
      acc.occupiedUnits += stats.occupiedUnits || 0
      acc.uniqueTenants += stats.uniqueTenants || 0
    }
    return acc
  }, { totalUnits: 0, occupiedUnits: 0, uniqueTenants: 0 })

  const hasStats = Object.keys(facilityStats).length > 0
  const overallOccupancy = totals.totalUnits > 0
    ? Math.round((totals.occupiedUnits / totals.totalUnits) * 100)
    : 0

  const agents = [
    {
      id: 'facility-audit',
      name: 'Facility Audit',
      description: 'Run automated checks for missing leases, expired insurance, billing errors, and more',
      icon: Building2,
      active: true,
      path: '/audit'
    },
    {
      id: 'security-audit',
      name: 'Security Audit',
      description: 'Check for access control issues and security vulnerabilities',
      icon: Shield,
      active: false,
      path: '/security'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Dashboard Stats */}
        {!loadingFacilities && facilities.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Facility Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Total Units */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {hasStats ? totals.totalUnits.toLocaleString() : '-'}
                    </p>
                    <p className="text-sm text-gray-500">Total Units</p>
                  </div>
                </div>
              </div>

              {/* Occupied Units */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100">
                    <Home className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {hasStats ? totals.occupiedUnits.toLocaleString() : '-'}
                    </p>
                    <p className="text-sm text-gray-500">Occupied Units</p>
                  </div>
                </div>
              </div>

              {/* Unique Tenants */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-violet-100">
                    <UserCheck className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {hasStats ? totals.uniqueTenants.toLocaleString() : '-'}
                    </p>
                    <p className="text-sm text-gray-500">Unique Tenants</p>
                  </div>
                </div>
              </div>

              {/* Occupancy Rate */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <Activity className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {hasStats ? `${overallOccupancy}%` : '-'}
                    </p>
                    <p className="text-sm text-gray-500">Occupancy Rate</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-Facility Stats */}
            {facilities.length > 1 && (
              <div className="mt-4 card p-0 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facility</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Occupied</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tenants</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Occupancy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facilities.map(facility => {
                      const stats = facilityStats[facility.id]
                      const isLoading = loadingStats[facility.id]

                      return (
                        <tr key={facility.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-gray-900">{facility.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {isLoading ? (
                              <Loader2 className="w-4 h-4 text-gray-400 animate-spin ml-auto" />
                            ) : stats ? stats.totalUnits : '-'}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {isLoading ? '-' : stats ? stats.occupiedUnits : '-'}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {isLoading ? '-' : stats ? stats.uniqueTenants : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isLoading ? '-' : stats ? (
                              <span className={`font-medium ${
                                stats.occupancyRate >= 90 ? 'text-green-600' :
                                stats.occupancyRate >= 70 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {stats.occupancyRate}%
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Loading state */}
        {loadingFacilities && (
          <div className="card text-center py-8">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-2" />
            <p className="text-gray-500">Loading facilities...</p>
          </div>
        )}

        {/* Lease Status Widget */}
        {!loadingFacilities && facilities.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Lease Verification</h2>
              {facilities.length > 1 && (
                <select
                  value={selectedLeaseStatusFacility?.id || ''}
                  onChange={(e) => {
                    const facility = facilities.find(f => f.id === e.target.value)
                    setSelectedLeaseStatusFacility(facility)
                  }}
                  className="input py-1 px-2 text-sm w-auto"
                >
                  {facilities.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              )}
            </div>
            <LeaseStatus
              facilityId={selectedLeaseStatusFacility?.id}
              facilityName={selectedLeaseStatusFacility?.name}
            />
          </div>
        )}

        {/* Agent Selection */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select an Agent</h2>
          <div className="grid gap-4">
            {agents.map(agent => (
              <button
                key={agent.id}
                onClick={() => agent.active && navigate(agent.path)}
                disabled={!agent.active}
                className={`card flex items-center gap-4 text-left transition-all
                           ${agent.active
                             ? 'hover:border-indigo-300 hover:shadow-md cursor-pointer'
                             : 'opacity-60 cursor-not-allowed bg-gray-50'}`}
              >
                <div className={`p-3 rounded-xl ${agent.active
                  ? 'bg-gradient-to-r from-indigo-500 to-violet-500 shadow-md'
                  : 'bg-gray-200'}`}>
                  <agent.icon className={`w-6 h-6 ${agent.active ? 'text-white' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{agent.name}</h3>
                    {!agent.active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{agent.description}</p>
                </div>
                {agent.active && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Layout with Navigation
// =============================================================================

function Layout({ children }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const isActive = (path) => location.pathname.startsWith(path)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button
              onClick={() => navigate('/agents')}
              className="flex items-center gap-2 text-gray-900 hover:text-indigo-600 transition-colors"
            >
              <div className="p-1.5 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Edge Agent Monitor</span>
            </button>

            {/* Navigation Links */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/agents')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                           ${isActive('/agents')
                             ? 'bg-indigo-50 text-indigo-700'
                             : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                Agents
              </button>
              <button
                onClick={() => navigate('/settings')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                           ${isActive('/settings')
                             ? 'bg-indigo-50 text-indigo-700'
                             : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                <Settings className="w-4 h-4" />
              </button>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <button
                  onClick={() => navigate('/users')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                             ${isActive('/users')
                               ? 'bg-indigo-50 text-indigo-700'
                               : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  <Users className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full ring-2 ring-gray-100"
                />
              )}
              <button
                onClick={logout}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {children}
      </main>
    </div>
  )
}

// =============================================================================
// Main App Component
// =============================================================================

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <Layout>
              <AgentSelectionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <Layout>
              <AuditDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <FacilitySettings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRole="manager">
            <Layout>
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/agents" replace />} />
      <Route path="*" element={<Navigate to="/agents" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
